import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Trash2, FolderKanban, Users, Calendar as CalIcon } from 'lucide-react';

function getWeekStart(d) {
  const date = new Date(d);
  const day = date.getDay();
  date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const PROJECT_COLORS = ['border-l-blue-500','border-l-emerald-500','border-l-violet-500','border-l-rose-500','border-l-cyan-500','border-l-orange-500','border-l-pink-500','border-l-teal-500','border-l-yellow-500'];
const DOT_COLORS = ['bg-blue-500','bg-emerald-500','bg-violet-500','bg-rose-500','bg-cyan-500','bg-orange-500','bg-pink-500','bg-teal-500','bg-yellow-500'];
const USER_COLORS = ['bg-amber-500','bg-blue-500','bg-emerald-500','bg-rose-500','bg-violet-500','bg-cyan-500','bg-orange-500','bg-pink-500','bg-teal-500'];
const ABSENCE_COLORS = { urlaub: 'bg-blue-500/30 border-blue-500/40 text-blue-300', krank: 'bg-rose-500/30 border-rose-500/40 text-rose-300', schulung: 'bg-violet-500/30 border-violet-500/40 text-violet-300', weiterbildung: 'bg-violet-500/30 border-violet-500/40 text-violet-300' };
const ABSENCE_LABELS = { urlaub: 'Urlaub', krank: 'Krank', schulung: 'Schulung', weiterbildung: 'Weiterbildung' };

export default function Einsatzplanung() {
  const { API, user: currentUser, isAdmin } = useAuth();
  const canSeeAll = isAdmin || currentUser?.role === 'projektleiter';
  const canEditPlan = isAdmin || currentUser?.role === 'projektleiter';
  const [view, setView] = useState('projekte'); // 'projekte' | 'plantafel'
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createCtx, setCreateCtx] = useState({ projectId: '', userId: '', date: '' });
  const [form, setForm] = useState({ user_id: '', project_id: '', start_time: '08:00', end_time: '16:00', notes: '' });
  const [availableUsers, setAvailableUsers] = useState([]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const dateFrom = fmtDate(days[0]);
  const dateTo = fmtDate(days[6]);
  const todayStr = fmtDate(new Date());

  useEffect(() => {
    axios.get(`${API}/assignments?date_from=${dateFrom}&date_to=${dateTo}`).then(r => setAssignments(r.data)).catch(() => {});
  }, [API, dateFrom, dateTo]);

  useEffect(() => {
    Promise.all([axios.get(`${API}/users`), axios.get(`${API}/projects`)])
      .then(([u, p]) => { setUsers(u.data); setProjects(p.data); }).catch(() => {});
  }, [API]);

  // Load absences for the week
  useEffect(() => {
    axios.get(`${API}/vacation-requests`).then(r => {
      setAbsences(r.data.filter(v => v.status === 'genehmigt'));
    }).catch(() => {});
  }, [API]);

  const openCreate = async (opts) => {
    setEditingAssignment(null);
    setCreateCtx(opts);
    setForm({ user_id: opts.userId || '', project_id: opts.projectId || '', start_time: '08:00', end_time: '16:00', notes: '' });
    try {
      const res = await axios.get(`${API}/users/available?date=${opts.date}`);
      setAvailableUsers(res.data);
    } catch { setAvailableUsers(users.map(u => ({ ...u, available: true }))); }
    setShowCreate(true);
  };

  const handleCreate = async () => { await handleSave(); };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/assignments/${id}`);
    setAssignments(prev => prev.filter(a => a.id !== id));
    toast.success('Gelöscht');
  };

  const [editingAssignment, setEditingAssignment] = useState(null);

  const openEditAssignment = (a) => {
    setEditingAssignment(a);
    setCreateCtx({ projectId: a.project_id, userId: a.user_id, date: a.date });
    setForm({ user_id: a.user_id, project_id: a.project_id, start_time: a.start_time || '08:00', end_time: a.end_time || '16:00', notes: a.notes || '' });
    setAvailableUsers(users.map(u => ({ ...u, available: true })));
    setShowCreate(true);
  };

  const handleSave = async () => {
    const uid = form.user_id || createCtx.userId;
    const pid = form.project_id || createCtx.projectId;
    if (!uid || !pid) { toast.error('Mitarbeiter und Projekt auswählen'); return; }

    try {
      if (editingAssignment) {
        const res = await axios.put(`${API}/assignments/${editingAssignment.id}`, {
          project_id: pid, user_id: uid, date: createCtx.date,
          start_time: form.start_time, end_time: form.end_time, notes: form.notes,
        });
        setAssignments(prev => prev.map(a => a.id === editingAssignment.id ? res.data : a));
        toast.success('Einsatz aktualisiert');
      } else {
        const res = await axios.post(`${API}/assignments`, {
          project_id: pid, user_id: uid, date: createCtx.date,
          start_time: form.start_time, end_time: form.end_time, notes: form.notes,
        });
        setAssignments(prev => [...prev, res.data]);
        toast.success('Einsatz erstellt');
      }
      setShowCreate(false);
      setEditingAssignment(null);
    } catch { toast.error('Fehler'); }
  };

  const getUserName = (id) => users.find(u => u.id === id)?.name || '?';
  const getUserInitial = (id) => (users.find(u => u.id === id)?.name || '?').charAt(0).toUpperCase();
  const getProjectName = (id) => projects.find(p => p.id === id)?.name || '?';
  const getProjectIdx = (id) => projects.findIndex(p => p.id === id);

  const getAbsenceForDay = (userId, dateStr) => {
    return absences.find(a => a.user_id === userId && a.start_date <= dateStr && a.end_date >= dateStr);
  };

  // ─── Drag & Drop ───
  const [dragItem, setDragItem] = useState(null);

  const handleDragStart = (e, assignment) => {
    setDragItem(assignment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', assignment.id);
  };

  const handleDragEnd = () => {
    setDragItem(null);
  };

  const handleDrop = async (e, targetProjectId, targetUserId, targetDate) => {
    e.preventDefault();
    if (!dragItem) return;
    const sameProject = targetProjectId ? dragItem.project_id === targetProjectId : true;
    const sameUser = targetUserId ? dragItem.user_id === targetUserId : true;
    const sameDate = dragItem.date === targetDate;
    if (sameProject && sameUser && sameDate) { setDragItem(null); return; }

    try {
      const update = {};
      if (targetProjectId) update.project_id = targetProjectId;
      if (targetUserId) update.user_id = targetUserId;
      if (targetDate) update.date = targetDate;
      const res = await axios.put(`${API}/assignments/${dragItem.id}`, update);
      setAssignments(prev => prev.map(a => a.id === dragItem.id ? res.data : a));
      toast.success('Einsatz verschoben');
    } catch { toast.error('Verschieben fehlgeschlagen'); }
    setDragItem(null);
  };

  const prevWeek = () => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; });
  const nextWeek = () => setWeekStart(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; });
  const goToday = () => setWeekStart(getWeekStart(new Date()));

  // ─── Shared Header ───
  const Header = () => (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="flex items-center gap-3">
        <h1 className="text-3xl font-bold text-white tracking-tight">Einsatzplanung</h1>
        <div className="flex bg-slate-900 border border-slate-800 rounded-sm p-0.5">
          <button onClick={() => setView('projekte')} data-testid="view-projekte"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${view === 'projekte' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>
            <FolderKanban className="w-3.5 h-3.5" />Projekte
          </button>
          {canSeeAll && (
            <>
              <button onClick={() => setView('plantafel')} data-testid="view-plantafel"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${view === 'plantafel' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>
                <Users className="w-3.5 h-3.5" />Plantafel
              </button>
              <button onClick={() => setView('kalender')} data-testid="view-kalender"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${view === 'kalender' ? 'bg-amber-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>
                <CalIcon className="w-3.5 h-3.5" />Kalender
              </button>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={goToday} className="text-amber-400 hover:bg-amber-500/10 text-xs font-mono">Heute</Button>
        <Button variant="ghost" size="icon" onClick={prevWeek} className="text-slate-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
        <span className="text-sm font-medium text-slate-300 min-w-[200px] text-center font-mono">
          {days[0].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} — {days[6].toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </span>
        <Button variant="ghost" size="icon" onClick={nextWeek} className="text-slate-400 hover:text-white"><ChevronRight className="w-5 h-5" /></Button>
      </div>
    </div>
  );

  // ─── Day Headers ───
  const DayHeaders = ({ labelWidth = '200px' }) => (
    <div className="grid min-w-[900px] border-b border-slate-800/60" style={{ gridTemplateColumns: `${labelWidth} repeat(7, 1fr)` }}>
      <div className="px-4 py-3 bg-slate-900/80 text-xs font-mono uppercase text-slate-500 tracking-wider border-r border-slate-800/40">
        {view === 'projekte' ? 'Projekt' : 'Mitarbeiter'}
      </div>
      {days.map((day, i) => {
        const ds = fmtDate(day);
        const isToday = ds === todayStr;
        const isWeekend = i >= 5;
        return (
          <div key={ds} className={`px-2 py-3 text-center border-r border-slate-800/40 last:border-r-0 ${isToday ? 'bg-amber-500/5' : isWeekend ? 'bg-slate-950/30' : 'bg-slate-900/80'}`}>
            <span className={`text-xs font-medium ${isToday ? 'text-amber-400' : 'text-slate-500'}`}>{DAYS[i]}</span>
            <span className={`text-sm font-bold ml-1.5 ${isToday ? 'text-amber-400' : 'text-slate-300'}`}>{day.getDate()}.{day.getMonth()+1}</span>
          </div>
        );
      })}
    </div>
  );

  // ─── Projekt-Ansicht ───
  const ProjektView = () => (
    <div className="bg-slate-900/30 border border-slate-800/60 rounded-sm overflow-x-auto">
      <DayHeaders />
      {projects.filter(p => p.status !== 'abgeschlossen').map((project, pi) => {
        const colorIdx = pi % PROJECT_COLORS.length;
        return (
          <div key={project.id} className="grid min-w-[900px] border-b border-slate-800/40 last:border-b-0" style={{ gridTemplateColumns: '200px repeat(7, 1fr)' }}>
            <div className={`px-4 py-3 bg-slate-900/50 border-r border-slate-800/40 flex items-start gap-2 border-l-[3px] ${PROJECT_COLORS[colorIdx]}`}>
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${DOT_COLORS[colorIdx]}`} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{project.name}</p>
                <Badge className="text-[10px] bg-slate-800 text-slate-400 border-slate-700 rounded-sm mt-1">{project.status}</Badge>
              </div>
            </div>
            {days.map((day, di) => {
              const ds = fmtDate(day);
              const isToday = ds === todayStr;
              const isWeekend = di >= 5;
              const cellAssignments = assignments.filter(a => a.project_id === project.id && a.date === ds);
              return (
                <div key={ds} className={`px-1.5 py-1.5 border-r border-slate-800/40 last:border-r-0 h-[80px] overflow-y-auto group
                  ${isToday ? 'bg-amber-500/[0.03]' : isWeekend ? 'bg-slate-950/20' : ''}
                  ${dragItem ? 'transition-colors' : ''}`}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('bg-amber-500/10'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('bg-amber-500/10'); }}
                  onDrop={e => { e.currentTarget.classList.remove('bg-amber-500/10'); handleDrop(e, project.id, null, ds); }}>
                  <div className="space-y-0.5">
                    {cellAssignments.map(a => (
                      <div key={a.id} draggable={canEditPlan ? "true" : undefined}
                        onDragStart={canEditPlan ? (e => handleDragStart(e, a)) : undefined} onDragEnd={canEditPlan ? handleDragEnd : undefined}
                        onClick={canEditPlan ? (() => openEditAssignment(a)) : undefined}
                        className={`flex items-center gap-1 bg-slate-800/70 border border-slate-700/50 rounded-sm px-1.5 py-0.5 group/chip relative z-10 select-none ${canEditPlan ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}>
                        <div className={`w-4 h-4 rounded-sm ${DOT_COLORS[colorIdx]} flex items-center justify-center text-[9px] font-bold text-white shrink-0`}>{getUserInitial(a.user_id)}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-medium text-slate-200 truncate">{getUserName(a.user_id)}</p>
                          <p className="text-[8px] font-mono text-slate-500">{a.start_time}–{a.end_time}</p>
                        </div>
                        {canEditPlan && <button onClick={e => { e.stopPropagation(); handleDelete(a.id); }} className="opacity-0 group-hover/chip:opacity-100 text-slate-500 hover:text-rose-400 transition-opacity shrink-0"><Trash2 className="w-3 h-3" /></button>}
                      </div>
                    ))}
                  </div>
                  {canEditPlan && cellAssignments.length === 0 && (
                    <button onClick={() => openCreate({ projectId: project.id, date: ds })}
                      className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus className="w-4 h-4 text-slate-600 hover:text-amber-400" />
                    </button>
                  )}
                  {canEditPlan && cellAssignments.length > 0 && (
                    <button onClick={() => openCreate({ projectId: project.id, date: ds })}
                      className="w-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                      <Plus className="w-3 h-3 text-slate-600 hover:text-amber-400" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      {projects.filter(p => p.status !== 'abgeschlossen').length === 0 && (
        <div className="p-12 text-center text-slate-500">Keine aktiven Projekte</div>
      )}
    </div>
  );

  // ─── Plantafel (Mitarbeiter-Ansicht) ───
  const PlantafelView = () => (
    <div className="bg-slate-900/30 border border-slate-800/60 rounded-sm overflow-x-auto">
      <DayHeaders />
      {users.map((u, ui) => {
        const uColor = USER_COLORS[ui % USER_COLORS.length];
        return (
          <div key={u.id} data-testid={`plantafel-row-${u.id}`}
            className="grid min-w-[900px] border-b border-slate-800/40 last:border-b-0" style={{ gridTemplateColumns: '200px repeat(7, 1fr)' }}>
            {/* User label */}
            <div className="px-4 py-3 bg-slate-900/50 border-r border-slate-800/40 flex items-center gap-3">
              <div className={`w-8 h-8 rounded-sm ${uColor} flex items-center justify-center text-slate-950 font-bold text-xs shrink-0`}>
                {u.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{u.name}</p>
                <p className="text-[10px] text-slate-500 capitalize">{u.role}</p>
              </div>
            </div>

            {/* Day cells */}
            {days.map((day, di) => {
              const ds = fmtDate(day);
              const isToday = ds === todayStr;
              const isWeekend = di >= 5;
              const cellAssignments = assignments.filter(a => a.user_id === u.id && a.date === ds);
              const absence = getAbsenceForDay(u.id, ds);

              return (
                <div key={ds} className={`px-1.5 py-1.5 border-r border-slate-800/40 last:border-r-0 h-[80px] overflow-y-auto group
                  ${isToday ? 'bg-amber-500/[0.03]' : isWeekend ? 'bg-slate-950/20' : ''}`}
                  onDragOver={e => { if (!absence) { e.preventDefault(); e.currentTarget.classList.add('bg-amber-500/10'); } }}
                  onDragLeave={e => { e.currentTarget.classList.remove('bg-amber-500/10'); }}
                  onDrop={e => { e.currentTarget.classList.remove('bg-amber-500/10'); if (!absence) handleDrop(e, null, u.id, ds); }}>

                  {/* Absence bar */}
                  {absence && (
                    <div className={`rounded-sm px-2 py-1 border text-[10px] font-medium ${ABSENCE_COLORS[absence.type] || 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
                      {ABSENCE_LABELS[absence.type] || absence.type}
                    </div>
                  )}

                  {/* Assignments */}
                  {!absence && (
                    <div className="space-y-0.5">
                      {cellAssignments.map(a => {
                        const pidx = getProjectIdx(a.project_id);
                        const pColor = DOT_COLORS[pidx >= 0 ? pidx % DOT_COLORS.length : 0];
                        return (
                          <div key={a.id} draggable={canEditPlan ? "true" : undefined}
                            onDragStart={canEditPlan ? (e => handleDragStart(e, a)) : undefined} onDragEnd={canEditPlan ? handleDragEnd : undefined}
                            onClick={canEditPlan ? (() => openEditAssignment(a)) : undefined}
                            className={`flex items-center gap-1 bg-slate-800/70 border border-slate-700/50 rounded-sm px-1.5 py-0.5 group/chip relative z-10 select-none ${canEditPlan ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}>
                            <div className={`w-2 h-2 rounded-full ${pColor} shrink-0`} />
                            <div className="min-w-0 flex-1">
                              <p className="text-[10px] font-medium text-slate-200 truncate">{getProjectName(a.project_id)}</p>
                              <p className="text-[8px] font-mono text-slate-500">{a.start_time}–{a.end_time}</p>
                            </div>
                            {canEditPlan && <button onClick={e => { e.stopPropagation(); handleDelete(a.id); }} className="opacity-0 group-hover/chip:opacity-100 text-slate-500 hover:text-rose-400 transition-opacity shrink-0"><Trash2 className="w-3 h-3" /></button>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Add button */}
                  {canEditPlan && !absence && cellAssignments.length === 0 && (
                    <button onClick={() => openCreate({ userId: u.id, date: ds })}
                      className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Plus className="w-4 h-4 text-slate-600 hover:text-amber-400" />
                    </button>
                  )}
                  {canEditPlan && !absence && cellAssignments.length > 0 && (
                    <button onClick={() => openCreate({ userId: u.id, date: ds })}
                      className="w-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                      <Plus className="w-3 h-3 text-slate-600 hover:text-amber-400" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  // ─── Legende ───
  const Legende = () => (
    <div className="flex items-center gap-4 flex-wrap text-xs text-slate-500">
      <span className="font-mono uppercase tracking-wider">Legende:</span>
      {view === 'projekte' ? (
        projects.filter(p => p.status !== 'abgeschlossen').map((p, i) => (
          <span key={p.id} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${DOT_COLORS[i % DOT_COLORS.length]}`} />{p.name}</span>
        ))
      ) : (
        <>
          {Object.entries(ABSENCE_LABELS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${k === 'urlaub' ? 'bg-blue-500' : k === 'krank' ? 'bg-rose-500' : 'bg-violet-500'}`} />{v}</span>
          ))}
        </>
      )}
    </div>
  );

  // ─── Kalender View (inline) ───
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; });
  const [calData, setCalData] = useState({ assignments: [], vacations: [], users: {}, projects: {} });

  useEffect(() => {
    if (view === 'kalender') {
      axios.get(`${API}/calendar?month=${calMonth}`).then(r => setCalData(r.data)).catch(() => {});
    }
  }, [API, calMonth, view]);

  const KalenderView = () => {
    const [yr, mo] = calMonth.split('-').map(Number);
    const daysInMonth = new Date(yr, mo, 0).getDate();
    const startOff = (new Date(yr, mo-1, 1).getDay() + 6) % 7;
    const calToday = fmtDate(new Date());
    const cells = [];
    for (let i = 0; i < startOff; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    const CDAYS = ['Mo','Di','Mi','Do','Fr','Sa','So'];
    const ABS_COLORS = { urlaub: 'bg-blue-500', krank: 'bg-rose-500', schulung: 'bg-violet-500', weiterbildung: 'bg-violet-500' };
    const getEv = (ds) => {
      const ev = [];
      (calData.assignments||[]).filter(a => a.date === ds).forEach(a => ev.push({ label: calData.users?.[a.user_id] || '?', sub: calData.projects?.[a.project_id] || '', color: 'bg-amber-500' }));
      (calData.vacations||[]).filter(v => v.start_date <= ds && v.end_date >= ds).forEach(v => ev.push({ label: v.user_name, sub: v.type, color: ABS_COLORS[v.type] || 'bg-blue-500' }));
      return ev;
    };
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-3 justify-center">
          <Button variant="ghost" size="icon" onClick={() => { const d = new Date(yr, mo-2, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }} className="text-slate-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
          <span className="text-lg font-semibold text-white min-w-[180px] text-center capitalize">{new Date(yr, mo-1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</span>
          <Button variant="ghost" size="icon" onClick={() => { const d = new Date(yr, mo, 1); setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }} className="text-slate-400 hover:text-white"><ChevronRight className="w-5 h-5" /></Button>
        </div>
        <div className="flex gap-3 text-xs text-slate-500"><span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" />Einsatz</span><span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" />Urlaub</span><span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" />Krank</span><span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-violet-500" />Schulung</span></div>
        <div className="bg-slate-900/50 border border-slate-800/60 rounded-sm overflow-hidden">
          <div className="grid grid-cols-7">{CDAYS.map(d => <div key={d} className="px-2 py-2 text-center text-xs font-mono uppercase text-slate-500 tracking-wider border-b border-slate-800/40 bg-slate-900/80">{d}</div>)}</div>
          <div className="grid grid-cols-7">{cells.map((day, i) => {
            if (!day) return <div key={i} className="min-h-[80px] border-b border-r border-slate-800/30 bg-slate-950/30" />;
            const ds = `${yr}-${String(mo).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
            const isT = ds === calToday;
            const events = getEv(ds);
            return (<div key={i} className={`min-h-[80px] border-b border-r border-slate-800/30 p-1 ${isT ? 'bg-amber-500/5' : (i%7)>=5 ? 'bg-slate-950/20' : ''}`}>
              <span className={`text-xs font-mono ${isT ? 'text-amber-400 font-bold' : 'text-slate-500'}`}>{day}</span>
              <div className="mt-1 space-y-0.5">{events.slice(0,3).map((ev,j) => <div key={j} className={`${ev.color} rounded-sm px-1 py-0.5 text-[9px] text-white truncate`}>{ev.label}{ev.sub ? ` · ${ev.sub}` : ''}</div>)}{events.length > 3 && <span className="text-[9px] text-slate-500">+{events.length-3}</span>}</div>
            </div>);
          })}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="einsatzplanung-page">
      <Header />
      {view === 'projekte' && <ProjektView />}
      {view === 'plantafel' && canSeeAll && <PlantafelView />}
      {view === 'kalender' && canSeeAll && <KalenderView />}
      {view === 'projekte' && projects.length > 0 && <Legende />}

      {/* Create/Edit Dialog — works for both views */}
      <Dialog open={showCreate} onOpenChange={v => { setShowCreate(v); if (!v) setEditingAssignment(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingAssignment ? 'Einsatz bearbeiten' : 'Einsatz planen'} — {createCtx.date?.split('-').reverse().join('.')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* User select (pre-filled from Plantafel, selectable from Projekt-View) */}
            <div>
              <Label className="text-slate-300">Mitarbeiter</Label>
              {availableUsers.some(u => !u.available) && (
                <p className="text-[10px] text-amber-400 mt-1 mb-1">Abwesende Mitarbeiter sind nicht auswählbar.</p>
              )}
              <Select value={form.user_id || createCtx.userId || ''} onValueChange={v => setForm({ ...form, user_id: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue placeholder="Mitarbeiter auswählen..." /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {availableUsers.map(u => {
                    const absLabels = { krank: 'Krank', urlaub: 'Urlaub', schulung: 'Schulung', weiterbildung: 'Weiterbildung' };
                    const pendLabels = { urlaub: 'Urlaub beantragt' };
                    return (
                      <SelectItem key={u.id} value={u.id} disabled={!u.available}
                        className={!u.available ? 'text-slate-600 opacity-60' : 'text-slate-200'}>
                        <span className="flex items-center gap-2">
                          {u.name}
                          {!u.available && <span className="text-[10px] text-rose-400">({absLabels[u.absence_type] || 'Abwesend'})</span>}
                          {u.available && u.has_pending && <span className="text-[10px] text-amber-400">({pendLabels[u.pending_type] || 'Antrag offen'})</span>}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Project select (pre-filled from Projekt-View, selectable from Plantafel) */}
            <div>
              <Label className="text-slate-300">Projekt</Label>
              <Select value={form.project_id || createCtx.projectId || ''} onValueChange={v => setForm({ ...form, project_id: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200 mt-1"><SelectValue placeholder="Projekt auswählen..." /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {projects.filter(p => p.status !== 'abgeschlossen').map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-slate-200">{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Von</Label>
              <Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
              <div><Label className="text-slate-300">Bis</Label>
              <Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})}
                className="bg-slate-950 border-slate-800 text-slate-200 mt-1" /></div>
            </div>
            <div><Label className="text-slate-300">Notizen</Label>
            <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional"
              className="bg-slate-950 border-slate-800 text-slate-200 placeholder:text-slate-600 mt-1" /></div>
            <Button onClick={handleCreate} className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-bold rounded-sm">
              {editingAssignment ? 'Einsatz speichern' : 'Einsatz erstellen'}
            </Button>
            {editingAssignment && (
              <Button variant="destructive" size="sm" onClick={async () => { await handleDelete(editingAssignment.id); setShowCreate(false); setEditingAssignment(null); }}
                className="w-full bg-rose-500/10 text-rose-400 border border-rose-400/20 hover:bg-rose-500/20 rounded-sm">
                <Trash2 className="w-4 h-4 mr-2" />Einsatz löschen
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
